import { useMemo } from 'react';
import { flatMap, pipe, map, forEach } from 'remeda';
import {
  createDevice,
  deleteSingleUserDeviceToken,
  deleteUserDevice,
  getAllUserDeviceTokensSafe,
  getUserDevicesSafe,
} from '@services';
import type {
  Device,
  DeviceDYNCode,
  DeviceTokenGroup,
  DeviceWithTokens,
} from '@types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const devicesQueryKey = (userId?: string) => ['devices', userId];
const deviceCodesQueryKey = (userId?: string) => ['deviceCodes', userId];

/**
 * Unisce la lista dei dispositivi con i relativi gruppi di token/sessions.
 *
 * @param devices Array di dispositivi utente
 * @param tokenGroups Gruppi (opzionali) di token e sessioni associati ai dispositivi
 * @returns Array di dispositivi arricchiti con i token e le sessioni associati
 */
const mergeDevicesWithCodes = (
  devices: Device[],
  tokenGroups?: DeviceTokenGroup[]
): DeviceWithTokens[] => {
  if (!devices.length) {
    // Nessun dispositivo: restituisce array vuoto
    return [];
  }

  if (!tokenGroups?.length) {
    // Nessun token/session associato: ogni device avrà tokens e sessions vuoti
    return pipe(
      devices,
      map(device => ({
        ...device,
        tokens: [],
        sessions: [],
      }))
    );
  }

  // Crea una mappa tra code del device e il relativo gruppo di token/sessions
  const tokensByDeviceCode = new Map<string, DeviceTokenGroup>();
  forEach(tokenGroups, group => {
    if (!group.deviceCode) return;
    tokensByDeviceCode.set(group.deviceCode, group);
  });

  return pipe(
    devices,
    map(device => {
      const lookupKey = device.code || device.clientId || '';
      const group = lookupKey ? tokensByDeviceCode.get(lookupKey) : undefined;

      return {
        ...device,
        tokens: group?.codes ?? [],
        sessions: group?.sessions ?? [],
      };
    })
  );
};

export const useUserDevices = (userId?: string) => {
  const devicesQuery = useQuery({
    queryKey: devicesQueryKey(userId),
    queryFn: () => getUserDevicesSafe(userId!),
    enabled: Boolean(userId),
    staleTime: 60_000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const deviceCodesQuery = useQuery({
    queryKey: deviceCodesQueryKey(userId),
    queryFn: () => getAllUserDeviceTokensSafe(userId!),
    enabled: Boolean(userId),
    staleTime: 60_000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const devices = useMemo<Device[]>(
    () => devicesQuery.data?.items ?? [],
    [devicesQuery.data]
  );
  const deviceTokens = useMemo<DeviceDYNCode[]>(
    () =>
      Array.from(
        pipe(
          deviceCodesQuery.data ?? [],
          flatMap(group => {
            // Crea un Set di sessionId dalle sessioni del gruppo
            const sessionIds = new Set(
              (group.sessions ?? [])
                .map(s => s.sessionId)
                .filter((id): id is string => Boolean(id))
            );

            // Filtra i codes per includere solo quelli con sessionId presente in sessions
            return (group.codes ?? []).filter(token => {
              if (!token.deviceCode) return false;
              const tokenSessionId = token.session?.split('#')[0];
              return tokenSessionId && sessionIds.has(tokenSessionId);
            });
          })
        )
      ),
    [deviceCodesQuery.data]
  );

  const devicesWithTokens = useMemo(
    () => mergeDevicesWithCodes(devices, deviceCodesQuery.data),
    [devices, deviceCodesQuery.data]
  );

  return {
    devices,
    devicesWithTokens,
    deviceTokens,
    isLoading: devicesQuery.isLoading || deviceCodesQuery.isLoading,
    isFetching: devicesQuery.isFetching || deviceCodesQuery.isFetching,
    error: devicesQuery.error ?? deviceCodesQuery.error,
    refetch: async () => {
      await Promise.all([devicesQuery.refetch(), deviceCodesQuery.refetch()]);
    },
  };
};

export const useCreateDeviceMutation = (userId?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (values: unknown) => createDevice(values, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: devicesQueryKey(userId) });
      queryClient.invalidateQueries({ queryKey: deviceCodesQueryKey(userId) });
    },
  });
};

export const useDeleteDeviceMutation = (userId: string | undefined) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (deviceId: string) => {
      if (!userId) throw new Error('User ID is required to delete device');
      return deleteUserDevice(userId, deviceId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: devicesQueryKey(userId) });
      queryClient.invalidateQueries({ queryKey: deviceCodesQueryKey(userId) });
    },
  });
};

export const useInvalidateSingleDeviceTokenMutation = (userId?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables: { deviceId: string; token: string }) => {
      if (!userId) throw new Error('User ID is required');
      const { deviceId, token } = variables;
      return deleteSingleUserDeviceToken(userId, deviceId, token);
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: deviceCodesQueryKey(userId) }),
  });
};
